import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/hooks';
import { AuthState } from '@/contexts';
import {
  ChevronDownIcon,
  UserCircleIcon,
  ArrowRightStartOnRectangleIcon,
  ShieldCheckIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

interface UserMenuProps {
  collapsed?: boolean;
  onClickOutsideParent: (expanded: boolean) => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({ collapsed = false, onClickOutsideParent }) => {
  const { user, logout, state } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef(null);

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
  };

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top - 200, // Much closer to button
        left: rect.left,
      });
    }
  }, [isOpen]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current &&
        !((event.clientX < dropdownRef.current.getBoundingClientRect().right && event.clientX > dropdownRef.current.getBoundingClientRect().left)
          && (event.clientY > dropdownRef.current.getBoundingClientRect().top && event.clientY < dropdownRef.current.getBoundingClientRect().bottom)
        )) {
        if (event.clientX > buttonRef.current.getBoundingClientRect().right) {
          onClickOutsideParent(false);
        }
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClickOutsideParent]);

  if (state !== AuthState.Authenticated || !user) {
    return null;
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${collapsed ? 'p-2 justify-center' : 'space-x-2 px-3 py-2'
          }`}
      >
        <UserCircleIcon className="h-8 w-8 text-gray-600 dark:text-gray-400" />
        {!collapsed && (
          <>
            <div className="text-left">
              <p className="text-sm font-medium text-gray-900 dark:text-white">{user.fullName}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {user.role === 'admin' ? 'Administrator' : 'User'}
              </p>
            </div>
            <ChevronDownIcon
              className={`h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </>
        )}
      </button>

      {/* Portal the dropdown to document.body to escape stacking context */}
      {isOpen && createPortal(
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-[100]" />

          {/* Dropdown - positioned using calculated coordinates */}
          <div
            className="fixed w-64 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-[110]"
            ref={dropdownRef}
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
          >
            <div className="p-4 border-b border-gray-100 dark:border-gray-800">
              <p className="text-sm font-medium text-gray-900 dark:text-white">{user.fullName}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{user.email}</p>
              <div className="mt-2 space-y-1">
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${user.isOnyxUser
                    ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300'
                    }`}
                >
                  {user.company || (user.isOnyxUser ? 'ONYX Insight' : 'External')}
                </span>
                <div>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${user.role === 'admin'
                      ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300'
                      : 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
                      }`}
                  >
                    {user.role === 'admin' ? (
                      <>
                        <ShieldCheckIcon className="h-3 w-3 mr-1" />
                        Administrator
                      </>
                    ) : (
                      <>
                        <UserIcon className="h-3 w-3 mr-1" />
                        User
                      </>
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-2">
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
              >
                <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
                <span>Sign out</span>
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};
